import React from 'react';
import { startCase } from 'lodash';
import { Label } from '@progress/kendo-react-labels';
import { Input, ColorPicker, Slider } from '@progress/kendo-react-inputs';
import { DropDownList, ListItemProps } from '@progress/kendo-react-dropdowns';

import { useReels } from '../../../../../contexts/reels';
import { hexToRGB, rgbaToHex } from '../../../../../utils/hex-to-rgb';

const fontFamily = [
  'Open Sans',
  'Roboto',
  'Lato',
  'Playfair Display',
  'Roboto Mono',
  'Nunito',
  'Verdana',
  'Tahoma',
  'Trebuchet MS',
  'Times New Roman',
  'Georgia',
  'Garamond',
  'Courier New'
];

const fontPosition = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' }
];

const fontBoxStyle = [
  { value: 'text_fit', label: 'Text Fit (classic subtitles)' },
  { value: 'full_frame', label: 'Full Frame (cinematic overlay)' }
];

const FontFamilyDropdown: React.FC<{
  value: string;
  onChange: (value: string) => void;
}> = ({ value, onChange }) => {
  const itemRender = (
    li: React.ReactElement<HTMLLIElement>,
    itemProps: ListItemProps
  ) => {
    const itemChildren = (
      <span style={{ fontFamily: `"${itemProps.dataItem}", sans-serif` }}>
        {itemProps.dataItem}
      </span>
    );

    return React.cloneElement(li, li.props, itemChildren);
  };

  const valueRender = (
    element: React.ReactElement<HTMLElement>,
    value: string
  ) => {
    if (!value) return element;

    return React.cloneElement(
      element,
      element.props,
      <span style={{ fontFamily: `"${value}", sans-serif` }}>{value}</span>
    );
  };

  return (
    <DropDownList
      data={fontFamily}
      fillMode={'outline'}
      value={value}
      onChange={(e) => onChange(e.value)}
      itemRender={itemRender}
      valueRender={valueRender}
    />
  );
};

export const TextSettings: React.FC = () => {
  const { global_settings, handleChange } = useReels();

  const textContainerAlign = React.useMemo(() => {
    if (global_settings.text_style.alignment === 'left') return 'start';
    if (global_settings.text_style.alignment === 'center') return 'center';
    if (global_settings.text_style.alignment === 'right') return 'end';
    return 'center';
  }, [global_settings.text_style.alignment]);

  return (
    <div className={'font-settings'}>
      <b
        style={{
          fontSize: '18px'
        }}>
        Text Styling
      </b>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: textContainerAlign,
          width: '100%',
          height: 'fit-content',
          padding: '8px',
          borderRadius: '8px',
          position: 'relative',
          backgroundColor: global_settings.background_color,
          ...((!global_settings.background_color ||
            global_settings.background_color === '') && {
            border: '1px solid black'
          })
        }}>
        {global_settings.text_style.box_style === 'full_frame' && (
          <div
            style={{
              position: 'absolute',
              zIndex: 0,
              inset: 0,
              backgroundColor:
                global_settings.text_style.box_color.split('@')[0] ?? '',
              opacity: global_settings.text_style.box_color.split('@')[1] ?? 0
            }}
          />
        )}

        <div
          className={'d-flex flex-column gap-2'}
          style={{
            zIndex: 1,
            fontFamily: `"${global_settings.font}"`,
            color: global_settings.text_style.color,
            textAlign: global_settings.text_style.alignment
          }}>
          {['Title text', 'Subtitle text', 'Body text'].map((text) => {
            const size = (text: string) => {
              switch (text) {
                case 'Title text':
                  return global_settings.text_style.title_font_size;
                case 'Subtitle text':
                  return global_settings.text_style.subtitle_font_size;

                default:
                  return global_settings.text_style.body_font_size;
              }
            };

            const boxStyle = () => {
              const style = global_settings.text_style.box_color.split('@');
              return { color: style[0] ?? '', opacity: style[1] ?? '' };
            };

            return (
              <div
                key={text}
                style={{
                  position: 'relative',
                  padding: 0,
                  margin: 0,
                  ...(global_settings.text_style.box_style === 'text_fit' && {
                    border: `${global_settings.text_style.box_border_width}px solid ${hexToRGB(
                      boxStyle().color
                    ).replace('1)', `${boxStyle().opacity})`)}`
                  })
                }}>
                {global_settings.text_style.box_style === 'text_fit' && (
                  <div
                    style={{
                      position: 'absolute',
                      zIndex: 0,
                      inset: 0,
                      backgroundColor:
                        global_settings.text_style.box_color.split('@')[0] ??
                        '',
                      opacity:
                        global_settings.text_style.box_color.split('@')[1] ?? ''
                    }}
                  />
                )}
                <div style={{ position: 'relative', fontSize: size(text) }}>
                  {text}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <Label>Font Family</Label>
        <FontFamilyDropdown
          value={global_settings.font}
          onChange={(value) =>
            handleChange({
              global_settings: {
                ...global_settings,
                font: value
              }
            })
          }
        />
      </div>

      <div className={'font-size-wrapper'}>
        {['title_font_size', 'subtitle_font_size', 'body_font_size'].map(
          (size: any) => (
            <div key={size}>
              <Label>{startCase(size)}</Label>
              <Input
                min={0}
                step={1}
                type={'number'}
                value={global_settings.text_style[size as 'title_font_size']}
                onChange={(e) =>
                  handleChange({
                    global_settings: {
                      ...global_settings,
                      text_style: {
                        ...global_settings.text_style,
                        [size]: parseInt(e.value)
                      }
                    }
                  })
                }
              />
            </div>
          )
        )}
      </div>

      <div>
        <Label>Text Alignment</Label>
        <DropDownList
          value={fontPosition.find(
            (q) => q.value === global_settings.text_style.alignment
          )}
          textField={'label'}
          dataItemKey={'value'}
          fillMode={'outline'}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                text_style: {
                  ...global_settings.text_style,
                  alignment: e.value.value
                }
              }
            })
          }
          data={fontPosition}
        />
      </div>

      <div className={'d-flex flex-column'}>
        <Label>Text Color</Label>
        <ColorPicker
          views={['gradient']}
          gradientSettings={{
            opacity: false,
            _adaptive: false
          }}
          flatColorPickerSettings={{
            activeView: 'gradient',
            format: 'hex'
          }}
          fillMode={'outline'}
          value={global_settings.text_style.color}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                text_style: {
                  ...global_settings.text_style,
                  color: rgbaToHex(e.value)
                }
              }
            })
          }
        />
      </div>

      <div className={'d-flex flex-column'}>
        <Label>Background Box Color</Label>
        <ColorPicker
          views={['gradient']}
          gradientSettings={{
            opacity: false,
            _adaptive: false
          }}
          flatColorPickerSettings={{
            activeView: 'gradient',
            format: 'hex'
          }}
          fillMode={'outline'}
          value={global_settings.text_style.box_color.split('@')[0] ?? ''}
          onChange={(e) => {
            const value = global_settings.text_style.box_color.split('@');
            value[0] = rgbaToHex(e.value);
            handleChange({
              global_settings: {
                ...global_settings,
                text_style: {
                  ...global_settings.text_style,
                  box_color: value.join('@')
                }
              }
            });
          }}
        />
      </div>

      {global_settings.text_style.box_style === 'text_fit' && (
        <div>
          <Label>Box Border Width</Label>
          <Input
            type={'number'}
            min={0}
            step={1}
            value={global_settings.text_style.box_border_width ?? 0}
            onChange={(e) =>
              handleChange({
                global_settings: {
                  ...global_settings,
                  text_style: {
                    ...global_settings.text_style,
                    box_border_width: parseInt(e.value)
                  }
                }
              })
            }
          />
        </div>
      )}

      <div className={'d-flex flex-column gap-1'}>
        <Label>Background Box Opacity</Label>
        <Slider
          min={0}
          max={1}
          step={0.05}
          className={'w-100'}
          value={
            parseFloat(global_settings.text_style.box_color.split('@')[1]) ??
            0.5
          }
          onChange={(e) => {
            const value = global_settings.text_style.box_color.split('@');
            value[1] = String(e.value.toFixed(2));
            handleChange({
              global_settings: {
                ...global_settings,
                text_style: {
                  ...global_settings.text_style,
                  box_color: value.join('@')
                }
              }
            });
          }}
        />
        <span
          style={{
            fontSize: '14px'
          }}>
          Controls the darkness of the text backdrop
        </span>
      </div>

      <div>
        <Label>Background Box Style</Label>
        <DropDownList
          value={fontBoxStyle.find(
            (q) => q.value === global_settings.text_style.box_style
          )}
          textField={'label'}
          dataItemKey={'value'}
          fillMode={'outline'}
          onChange={(e) =>
            handleChange({
              global_settings: {
                ...global_settings,
                text_style: {
                  ...global_settings.text_style,
                  box_style: e.value.value
                }
              }
            })
          }
          data={fontBoxStyle}
        />
      </div>
    </div>
  );
};
